// src/pages/Settings.js
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const Settings = () => {
  const [letterheadFile, setLetterheadFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [status, setStatus] = useState({ message: '', type: '' });

  const [currentLetterhead, setCurrentLetterhead] = useState(
    () => localStorage.getItem('letterheadBlobUrl') ||
          localStorage.getItem('letterheadUrl') || ''
  );
  const [currentSignature, setCurrentSignature] = useState(
    () => localStorage.getItem('signatureBlobUrl') ||
          localStorage.getItem('signatureUrl') || ''
  );

  const [brandingDetails, setBrandingDetails] = useState({
    hospitalName: '',
    address: '',
    phone: '',
    email: '',
    website: ''
  });

  const [clinician, setClinician] = useState({
    fullName: '',
    designation: ''
  });

  const persistAndBroadcast = useCallback((key, value) => {
    try {
      if (value === null || value === undefined) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, value);
        void localStorage.getItem(key);
      }
    } catch (e) {
      console.warn(`[Settings] Failed to persist ${key}:`, e);
    }

    try {
      window.dispatchEvent(
        new CustomEvent('settings-updated', { detail: { key, value } })
      );
    } catch (_) {}
  }, []);

  // ─── On mount: load branding + signatures + clinician ───
  useEffect(() => {
    const fetchSettingsData = async () => {
      try {
        const orgRes = await api.getOrganizationBranding();
        const org = orgRes?.data || orgRes;
        if (org) {
          setBrandingDetails({
            hospitalName: org.hospitalName || '',
            address: org.address || '',
            phone: org.phone || '',
            email: org.email || '',
            website: org.website || ''
          });

          if (org.letterheadUrl) {
            persistAndBroadcast('letterheadUrl', org.letterheadUrl);
            // Resolve a renderable blob URL
            try {
              const blobUrl = await api.fetchLetterheadBlobUrl();
              setCurrentLetterhead(blobUrl);
              persistAndBroadcast('letterheadBlobUrl', blobUrl);
            } catch (e) {
              console.warn('[Settings] letterhead blob failed:', e.message);
              setCurrentLetterhead(org.letterheadUrl);
            }
          }

          if (org.hospitalName) {
            persistAndBroadcast('hospitalName', org.hospitalName);
          }
        }
      } catch (error) {
        if (!String(error.message).includes('404')) {
          console.error('Could not fetch organization branding:', error);
        }
      }

      try {
        const sigRes = await api.getSignatures();
        const signatures = sigRes?.data || [];
        if (Array.isArray(signatures) && signatures.length > 0) {
          const defaultSig =
            signatures.find((s) => s.isDefault) || signatures[0];
          if (defaultSig && defaultSig.imageUrl) {
            persistAndBroadcast('signatureUrl', defaultSig.imageUrl);
            try {
              const blobUrl = await api.fetchSignatureBlobUrl(
                defaultSig.imageUrl
              );
              setCurrentSignature(blobUrl);
              persistAndBroadcast('signatureBlobUrl', blobUrl);
            } catch (e) {
              console.warn('[Settings] signature blob failed:', e.message);
              setCurrentSignature(defaultSig.imageUrl);
            }
          }
        }
      } catch (error) {
        if (!String(error.message).includes('404')) {
          console.error('Could not fetch signatures:', error);
        }
      }

      try {
        const meRes = await api.getMe();
        const me = meRes?.data || meRes;
        if (me) {
          const fullName =
            me.fullName ||
            [me.firstName, me.lastName].filter(Boolean).join(' ') ||
            me.username ||
            '';
          const designation =
            me.designation || me.role || me.department || '';
          setClinician({ fullName, designation });
          if (fullName) {
            persistAndBroadcast('clinicianName', fullName);
            persistAndBroadcast('clinicianFullName', fullName);
          }
          if (designation) {
            persistAndBroadcast('clinicianDesignation', designation);
          }
          if (me.hospitalName) {
            persistAndBroadcast('hospitalName', me.hospitalName);
          }
        }
      } catch (error) {
        console.warn('Could not fetch clinician profile:', error?.message);
      }
    };

    fetchSettingsData();
  }, [persistAndBroadcast]);

  const handleFileChange = (e, type) => {
    if (e.target.files && e.target.files[0]) {
      if (type === 'letterhead') setLetterheadFile(e.target.files[0]);
      if (type === 'signature') setSignatureFile(e.target.files[0]);
    }
  };

  const handleBrandingChange = (e) => {
    const { name, value } = e.target;
    setBrandingDetails((prev) => ({ ...prev, [name]: value }));
  };

  const saveBrandingText = async () => {
    setStatus({ message: 'Saving hospital details...', type: 'info' });
    try {
      const payload = {
        hospitalName: brandingDetails.hospitalName,
        address: brandingDetails.address,
        phone: brandingDetails.phone,
        email: brandingDetails.email,
        website: brandingDetails.website
      };
      const res = await api.updateOrganizationBranding(payload);
      const updated = res?.data || res;
      if (updated) {
        setBrandingDetails((prev) => ({
          ...prev,
          hospitalName: updated.hospitalName ?? prev.hospitalName,
          address: updated.address ?? prev.address,
          phone: updated.phone ?? prev.phone,
          email: updated.email ?? prev.email,
          website: updated.website ?? prev.website
        }));
        if (updated.hospitalName) {
          persistAndBroadcast('hospitalName', updated.hospitalName);
        }
        if (updated.letterheadUrl) {
          persistAndBroadcast('letterheadUrl', updated.letterheadUrl);
          try {
            const blobUrl = await api.fetchLetterheadBlobUrl();
            setCurrentLetterhead(blobUrl);
            persistAndBroadcast('letterheadBlobUrl', blobUrl);
          } catch (e) {
            console.warn('[Settings] letterhead blob refresh failed:', e.message);
          }
        }
      }
      if (clinician.fullName) {
        persistAndBroadcast('clinicianName', clinician.fullName);
        persistAndBroadcast('clinicianFullName', clinician.fullName);
      }
      setStatus({ message: 'Hospital details updated successfully!', type: 'success' });
    } catch (err) {
      console.error(err);
      setStatus({
        message: err.message || 'Failed to update hospital details.',
        type: 'error'
      });
    }
  };

  // ─── Upload Letterhead ───
  const uploadLetterheadToBackend = async () => {
    if (!letterheadFile) return;
    setStatus({ message: 'Uploading letterhead to server...', type: 'info' });

    try {
      const response = await api.uploadLetterhead(letterheadFile);
      const payload = response?.data || response;
      const rawUrl =
        payload?.letterheadUrl || payload?.url || payload?.imageUrl;

      if (!rawUrl) {
        setStatus({ message: 'Uploaded, but no URL was returned.', type: 'error' });
        return;
      }

      // 1. Persist the raw backend URL
      persistAndBroadcast('letterheadUrl', rawUrl);

      // 2. Fetch a renderable blob URL and persist that too
      let renderableUrl = rawUrl;
      try {
        renderableUrl = await api.fetchLetterheadBlobUrl();
        persistAndBroadcast('letterheadBlobUrl', renderableUrl);
      } catch (e) {
        console.warn('[Settings] Could not resolve letterhead blob:', e.message);
        // fall back to raw URL — may or may not render depending on backend
      }

      setCurrentLetterhead(renderableUrl);
      setStatus({ message: 'Letterhead uploaded successfully!', type: 'success' });
      setLetterheadFile(null);

      const input = document.getElementById('letterhead-input');
      if (input) input.value = '';
    } catch (error) {
      console.error('Letterhead upload error: ', error);
      setStatus({
        message: error.message || 'Failed to upload letterhead.',
        type: 'error'
      });
    }
  };

  // ─── Upload Signature ───
  const uploadSignatureToBackend = async () => {
    if (!signatureFile) return;
    setStatus({ message: 'Uploading signature to server...', type: 'info' });

    try {
      const response = await api.uploadSignature(
        signatureFile,
        'Primary Signature',
        true
      );
      const payload = response?.data || response;
      const rawUrl =
        payload?.imageUrl || payload?.url || payload?.signatureUrl;

      if (!rawUrl) {
        setStatus({ message: 'Uploaded, but no URL was returned.', type: 'error' });
        return;
      }

      persistAndBroadcast('signatureUrl', rawUrl);

      let renderableUrl = rawUrl;
      try {
        renderableUrl = await api.fetchSignatureBlobUrl(rawUrl);
        persistAndBroadcast('signatureBlobUrl', renderableUrl);
      } catch (e) {
        console.warn('[Settings] Could not resolve signature blob:', e.message);
      }

      setCurrentSignature(renderableUrl);
      setStatus({ message: 'Signature uploaded successfully!', type: 'success' });
      setSignatureFile(null);

      const input = document.getElementById('signature-input');
      if (input) input.value = '';
    } catch (error) {
      console.error('Signature upload error: ', error);
      setStatus({
        message: error.message || 'Failed to upload signature.',
        type: 'error'
      });
    }
  };

  // ─── Render (same as before) ───
  return (
    <div
      className="fade-in"
      style={{
        padding: '30px',
        maxWidth: '800px',
        margin: '0 auto',
        fontFamily: "'Inter', 'Segoe UI', sans-serif"
      }}
    >
      <div style={{ marginBottom: '30px' }}>
        <h2
          style={{
            margin: '0 0 8px 0',
            color: '#0f172a',
            fontSize: '1.8rem',
            fontWeight: '800'
          }}
        >
          Settings &amp; Branding
        </h2>
        <p style={{ margin: 0, color: '#64748b', fontSize: '0.95rem' }}>
          Configure your hospital's profile, letterhead, and clinician
          signature for exported reports.
        </p>
      </div>

      {status.message && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: '20px',
            borderRadius: '6px',
            fontSize: '0.9rem',
            fontWeight: '600',
            backgroundColor:
              status.type === 'error'
                ? '#fef2f2'
                : status.type === 'success'
                ? '#f0fdf4'
                : '#eff6ff',
            color:
              status.type === 'error'
                ? '#ef4444'
                : status.type === 'success'
                ? '#16a34a'
                : '#2563eb',
            border: `1px solid ${
              status.type === 'error'
                ? '#f87171'
                : status.type === 'success'
                ? '#86efac'
                : '#bfdbfe'
            }`
          }}
        >
          {status.message}
        </div>
      )}

      {/* TEXT BRANDING PROFILE SECTION */}
      <div
        style={{
          marginBottom: '30px',
          padding: '25px',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          background: '#ffffff',
          boxShadow: '0 4px 6px rgba(0,0,0,0.02)'
        }}
      >
        <h3
          style={{
            margin: '0 0 20px 0',
            color: '#1e293b',
            fontSize: '1.2rem',
            fontWeight: '600'
          }}
        >
          Hospital Profile Details
        </h3>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '15px',
            marginBottom: '15px'
          }}
        >
          <div>
            <label style={labelStyle}>Hospital Name</label>
            <input
              type="text"
              name="hospitalName"
              value={brandingDetails.hospitalName}
              onChange={handleBrandingChange}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Phone Number</label>
            <input
              type="text"
              name="phone"
              value={brandingDetails.phone}
              onChange={handleBrandingChange}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Email Address</label>
            <input
              type="email"
              name="email"
              value={brandingDetails.email}
              onChange={handleBrandingChange}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Website</label>
            <input
              type="text"
              name="website"
              value={brandingDetails.website}
              onChange={handleBrandingChange}
              style={inputStyle}
            />
          </div>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Address</label>
          <input
            type="text"
            name="address"
            value={brandingDetails.address}
            onChange={handleBrandingChange}
            style={inputStyle}
          />
        </div>

        <button
          onClick={saveBrandingText}
          style={{
            padding: '8px 16px',
            cursor: 'pointer',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontWeight: '600'
          }}
        >
          Update Profile Details
        </button>
      </div>

      {/* CLINICIAN IDENTITY PREVIEW */}
      {(clinician.fullName || clinician.designation) && (
        <div
          style={{
            marginBottom: '30px',
            padding: '20px 25px',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            background: '#f8fafc'
          }}
        >
          <h3
            style={{
              margin: '0 0 6px 0',
              color: '#1e293b',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            Clinician Identity (used on reports)
          </h3>
          <p style={{ margin: 0, color: '#475569', fontSize: '0.9rem' }}>
            {clinician.fullName ? `Dr. ${clinician.fullName}` : '—'}
            {clinician.designation ? ` • ${clinician.designation}` : ''}
          </p>
        </div>
      )}

      {/* IMAGE UPLOAD SECTION */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '20px'
        }}
      >
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>Upload Letterhead</h3>
          <p style={cardSubtitleStyle}>
            Overrides the text-based profile at the top of reports.
          </p>

          {currentLetterhead && (
            <div style={previewBoxStyle}>
              <img
                key={currentLetterhead}
                src={currentLetterhead}
                alt="Letterhead"
                style={{
                  maxHeight: '60px',
                  maxWidth: '100%',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  console.warn('[Settings] Letterhead image failed:', currentLetterhead);
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          <input
            id="letterhead-input"
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange(e, 'letterhead')}
            style={{ marginBottom: '15px', width: '100%', fontSize: '0.85rem' }}
          />
          <button
            onClick={uploadLetterheadToBackend}
            disabled={!letterheadFile}
            style={{
              width: '100%',
              padding: '10px',
              cursor: letterheadFile ? 'pointer' : 'not-allowed',
              background: letterheadFile ? '#3b82f6' : '#94a3b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600'
            }}
          >
            Upload to Server
          </button>
        </div>

        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>Upload Signature</h3>
          <p style={cardSubtitleStyle}>
            Your personal clinician signature for finalizing reports.
          </p>

          {currentSignature && (
            <div style={previewBoxStyle}>
              <img
                key={currentSignature}
                src={currentSignature}
                alt="Signature"
                style={{
                  maxHeight: '60px',
                  maxWidth: '100%',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  console.warn('[Settings] Signature image failed:', currentSignature);
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          <input
            id="signature-input"
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange(e, 'signature')}
            style={{ marginBottom: '15px', width: '100%', fontSize: '0.85rem' }}
          />
          <button
            onClick={uploadSignatureToBackend}
            disabled={!signatureFile}
            style={{
              width: '100%',
              padding: '10px',
              cursor: signatureFile ? 'pointer' : 'not-allowed',
              background: signatureFile ? '#3b82f6' : '#94a3b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600'
            }}
          >
            Upload to Server
          </button>
        </div>
      </div>
    </div>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: '600',
  color: '#475569',
  marginBottom: '5px'
};

const inputStyle = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid #cbd5e1',
  boxSizing: 'border-box'
};

const cardStyle = {
  padding: '25px',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  background: '#ffffff',
  boxShadow: '0 4px 6px rgba(0,0,0,0.02)'
};

const cardTitleStyle = {
  margin: '0 0 10px 0',
  color: '#1e293b',
  fontSize: '1.1rem',
  fontWeight: '600'
};

const cardSubtitleStyle = {
  margin: '0 0 20px 0',
  color: '#475569',
  fontSize: '0.85rem'
};

const previewBoxStyle = {
  marginBottom: '20px',
  padding: '10px',
  background: '#f8fafc',
  border: '1px dashed #cbd5e1',
  borderRadius: '8px'
};

export default Settings;